/**
 * Edit Profile Page - Assistant/Second Shooter Profile
 * Auto-saves data as user makes changes (debounced)
 */
import React, { useCallback, useRef, useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { NavBar, NavActions } from "@/components/NavBar";
import { Footer } from "@/components/Footer";
import { ArrowLeft, Camera, Briefcase, Settings, Award, Globe, DollarSign, Calendar, Check, AlertTriangle, Trash2, Power, CreditCard } from "lucide-react";

import { 
  LocationInput,
  BasicInfoSection,
  WorkTypesSection,
  SkillsSection,
  AvailabilitySection,
  ExperienceSection,
  GenresSection,
  ShootingStylesSection,
  NotableClientsSection,
  GearBagSection,
  AdditionalServicesSection,
  ProfessionalDetailsSection,
  LanguagesSection,
  CertificationsSection,
  PortfolioSection,
  ExternalLinksSection,
  PaymentsSection,
  VideoPortfolioSection,
  CalendarConnection
} from "@/components/profile";


function AccountManagement({ token, user, logout, navigate }) {
  const [password, setPassword] = useState("");
  const [action, setAction] = useState(null); // "deactivate" or "delete"
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [subStatus, setSubStatus] = useState(null);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);

  // Fetch subscription status
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/subscriptions/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setSubStatus(await res.json());
      } catch {}
    })();
  }, [token]);

  const handleCancelSubscription = async () => {
    if (!confirm("Cancel your subscription? It will remain active until the end of your billing period.")) return;
    setCancellingSubscription(true);
    try {
      const res = await fetch(`${API}/subscriptions/cancel`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || "Subscription cancelled");
        setSubStatus(prev => ({ ...prev, cancel_at_period_end: true, status: "cancelled" }));
      } else {
        toast.error(data.detail || "Failed to cancel subscription");
      }
    } catch {
      toast.error("Network error");
    }
    setCancellingSubscription(false);
  };

  const handleAction = async () => {
    if (!password) return toast.error("Enter your password to confirm");
    if (action === "delete" && !confirmed) return toast.error("Check the confirmation box first");
    setLoading(true);
    try {
      const endpoint = action === "delete" ? "/users/account/delete" : "/users/account/deactivate";
      const res = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(action === "delete" ? "Account deleted" : "Account deactivated");
        logout();
        navigate("/");
      } else {
        toast.error(data.detail || "Action failed");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setAction(null); setPassword(""); setConfirmed(false); };

  return (
    <div className="space-y-8" data-testid="account-management">
      <div>
        <h3 className="text-lg font-bold mb-1">Account Management</h3>
        <p className="text-white/50 text-sm">Manage your account status or delete your data.</p>
      </div>

      {!action && (
        <div className="space-y-4">
          {/* Subscription Management */}
          {subStatus && subStatus.has_subscription && (
            <div className="border border-white/10 rounded-sm p-5">
              <div className="flex items-start gap-4">
                <CreditCard className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="font-semibold mb-1">Subscription</h4>
                  <p className="text-white/50 text-sm leading-relaxed">
                    {subStatus.cancel_at_period_end
                      ? `Your subscription is cancelled and will end ${subStatus.current_period_end ? `on ${new Date(subStatus.current_period_end).toLocaleDateString()}` : "at the end of your billing period"}.`
                      : `You have an active subscription. Cancel anytime — it will remain active until the end of your billing period.`
                    }
                  </p>
                </div>
                {!subStatus.cancel_at_period_end && (
                  <Button
                    onClick={handleCancelSubscription}
                    disabled={cancellingSubscription}
                    variant="outline"
                    className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10 rounded-sm"
                    data-testid="cancel-subscription-btn"
                  >
                    {cancellingSubscription ? "Cancelling..." : "Cancel Subscription"}
                  </Button>
                )}
                {subStatus.cancel_at_period_end && (
                  <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded-sm">Cancelled</span>
                )}
              </div>
            </div>
          )}

          {/* Deactivate */}
          <div className="border border-white/10 rounded-sm p-5">
            <div className="flex items-start gap-4">
              <Power className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="font-semibold mb-1">Deactivate Account</h4>
                <p className="text-white/50 text-sm leading-relaxed">
                  Your profile will be hidden from search results and job matching. Your data is preserved, and you can reactivate anytime by contacting support.
                </p>
              </div>
              <Button
                onClick={() => setAction("deactivate")}
                variant="outline"
                className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10 rounded-sm"
                data-testid="deactivate-btn"
              >
                Deactivate
              </Button>
            </div>
          </div>

          {/* Delete */}
          <div className="border border-red-500/20 rounded-sm p-5">
            <div className="flex items-start gap-4">
              <Trash2 className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="font-semibold mb-1">Delete My Data</h4>
                <p className="text-white/50 text-sm leading-relaxed">
                  Permanently delete your account and all associated data, including applications, reviews, messages, and contracts. This cannot be undone.
                </p>
              </div>
              <Button
                onClick={() => setAction("delete")}
                variant="outline"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-sm"
                data-testid="delete-btn"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {action && (
        <div className="border border-red-500/30 rounded-sm p-6 bg-red-500/5" data-testid="confirm-action">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <h4 className="font-bold text-red-400">
              {action === "delete" ? "Permanently Delete Account" : "Deactivate Account"}
            </h4>
          </div>

          {action === "delete" && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-sm p-4 mb-4">
              <p className="text-sm text-red-300 leading-relaxed">
                This will permanently delete your profile, applications, reviews, messages, contracts, and all other data. This action cannot be reversed.
              </p>
            </div>
          )}

          {action === "deactivate" && (
            <p className="text-sm text-white/60 mb-4 leading-relaxed">
              Your account will be hidden from all search results and job matching. You can reactivate at any time by contacting support@gottasecond.com.
            </p>
          )}

          <div className="space-y-4">
            <div>
              <label className="text-sm text-white/60 block mb-1.5">Confirm your password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="w-full max-w-sm bg-white/[0.05] border border-white/10 text-white rounded-sm px-4 h-10 focus:border-white/40 outline-none"
                data-testid="confirm-password-input"
              />
            </div>

            {action === "delete" && (
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="mt-1"
                  data-testid="confirm-delete-checkbox"
                />
                <span className="text-sm text-white/60">I understand this is permanent and all my data will be deleted.</span>
              </label>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleAction}
                disabled={loading || !password || (action === "delete" && !confirmed)}
                className={`rounded-sm font-bold px-6 ${
                  action === "delete"
                    ? "bg-red-600 hover:bg-red-700 text-white"
                    : "bg-yellow-600 hover:bg-yellow-700 text-white"
                }`}
                data-testid="confirm-action-btn"
              >
                {loading ? "Processing..." : action === "delete" ? "Delete My Account" : "Deactivate My Account"}
              </Button>
              <Button
                onClick={reset}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10 rounded-sm px-6"
                data-testid="cancel-action-btn"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default function EditProfile() {
  const navigate = useNavigate();
  const { user, token, updateUser, logout } = useAuth();
  
  // Loading states
  const [initialLoading, setInitialLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const saveTimeoutRef = useRef(null);
  const isFirstLoad = useRef(true);

  // Basic info state
  const [name, setName] = useState("");
  const [bio, setBio] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [workRadius, setWorkRadius] = useState(50);
  const [willingToTravel, setWillingToTravel] = useState(false);
  
  // Active status + Communication Preferences
  const [isActive, setIsActive] = useState(true);
  const [prefNewJobs, setPrefNewJobs] = useState(true);
  const [prefNewMessages, setPrefNewMessages] = useState(true);
  const [prefAnnouncements, setPrefAnnouncements] = useState(true);

  // Role state
  const [roleAssistant, setRoleAssistant] = useState(true);
  const [roleStudio, setRoleStudio] = useState(false);

  // Assistant profile state
  const [yearsExperience, setYearsExperience] = useState(0);
  const [workTypes, setWorkTypes] = useState([]);
  const [skills, setSkills] = useState([]);
  const [generalAvailability, setGeneralAvailability] = useState("both");
  const [genres, setGenres] = useState([]);
  const [shootingStyles, setShootingStyles] = useState([]);
  const [editingStyles, setEditingStyles] = useState([]);
  const [lightingStyles, setLightingStyles] = useState([]);
  const [experienceLevel, setExperienceLevel] = useState("novice");
  const [employmentType, setEmploymentType] = useState("part_time");
  const [hasBeenLead, setHasBeenLead] = useState(false);
  const [notableClients, setNotableClients] = useState([]);
  const [gearItems, setGearItems] = useState([]);
  const [willingToRent, setWillingToRent] = useState(false);
  const [offersEditing, setOffersEditing] = useState(false);
  const [hasBackupGear, setHasBackupGear] = useState(false);
  const [hasInsurance, setHasInsurance] = useState(false);
  const [insuranceCoverage, setInsuranceCoverage] = useState("");
  const [hasTransportation, setHasTransportation] = useState(true);
  const [rateMin, setRateMin] = useState("");
  const [rateMax, setRateMax] = useState("");
  const [phone, setPhone] = useState("");
  const [portfolio, setPortfolio] = useState([]);
  const [portfolioLink, setPortfolioLink] = useState("");
  const [instagramHandle, setInstagramHandle] = useState("");
  const [facebookUrl, setFacebookUrl] = useState("");
  const [tiktokHandle, setTiktokHandle] = useState("");
  const [languages, setLanguages] = useState(["English"]);
  const [certifications, setCertifications] = useState([]);
  
  // Payment information state
  const [w9Uploaded, setW9Uploaded] = useState(false);
  const [w9FileName, setW9FileName] = useState("");
  const [paymentMethods, setPaymentMethods] = useState([]);
  
  // Video portfolio state
  const [portfolioVideos, setPortfolioVideos] = useState([]);

  // Save status indicator
  const [saveStatus, setSaveStatus] = useState(""); // "", "saving", "saved"

  // Opening a specific tab by URL, so other pages can link straight to the
  // account controls instead of telling people where to click.
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const KNOWN_TABS = ["basic", "experience", "gear", "professional", "portfolio", "payments", "studio", "account"];
  const [activeTab, setActiveTab] = useState(
    KNOWN_TABS.indexOf(requestedTab) !== -1 ? requestedTab : "basic"
  );

  // Helper to check current role selection
  const isAssistant = () => roleAssistant;
  const isStudio = () => roleStudio;

  // Get existing profile for preserving fields we don't edit
  const existingProfile = useMemo(() => {
    return profileData?.assistant_profile || user?.assistant_profile || {};
  }, [profileData, user]);

  // Fetch fresh profile data on mount
  useEffect(() => {
    async function fetchProfile() {
      if (!token) return;
      try {
        const res = await fetch(`${API}/users/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setProfileData(data);
          
          // Initialize all form fields with fetched data
          setName(data.name || "");
          setBio(data.bio || "");
          setProfileImage(data.profile_image || "");
          
          // Location: check location field, then top-level location_info (from registration), then assistant_profile fallback
          const loc = data.location || data.location_info || data.assistant_profile?.location_info || {};
          setCity(loc.city || "");
          setState(loc.state || "");
          setZipCode(loc.zip_code || "");
          setWorkRadius(loc.work_radius || 50);
          setIsActive(data.assistant_profile?.find_work_active !== false);
          setPhone(data.phone || "");
          
          const prefs = data.communication_preferences || {};
          setPrefNewJobs(prefs.new_jobs !== false);
          setPrefNewMessages(prefs.new_messages !== false);
          setPrefAnnouncements(prefs.announcements !== false);
          
          const roles = data.roles || [];
          setRoleAssistant(roles.includes("assistant"));
          setRoleStudio(roles.includes("studio"));
          
          // Assistant profile data
          const ap = data.assistant_profile || {};
          setYearsExperience(ap.years_experience || 0);
          // Migrate workTypes: if old format (flat strings), convert to objects
          const rawWorkTypes = ap.work_types || [];
          const migratedWorkTypes = rawWorkTypes.map(wt => {
            if (typeof wt === "string") {
              return { type: wt, rate_min: ap.rate_min || "", rate_max: ap.rate_max || "" };
            }
            return wt;
          });
          setWorkTypes(migratedWorkTypes);
          setSkills(ap.skills || []);
          setWillingToTravel(ap.willing_to_travel || false);
          setGeneralAvailability(ap.availability_settings?.general_availability || "both");
          setGenres(ap.genres || []);
          setShootingStyles(ap.shooting_styles || []);
          setEditingStyles(ap.editing_styles || []);
          setLightingStyles(ap.lighting_styles || []);
          setExperienceLevel(ap.experience_level || "novice");
          setEmploymentType(ap.employment_type || "part_time");
          setHasBeenLead(ap.has_been_lead || false);
          setNotableClients(ap.notable_clients || []);
          // Load gear from assistant profile, or fall back to studio profile if assistant has none
          const sp = data.studio_profile || {};
          setGearItems(ap.gear_items?.length > 0 ? ap.gear_items : (sp.gear_items || []));
          setWillingToRent(ap.willing_to_rent || false);
          setOffersEditing(ap.offers_editing || false);
          setHasBackupGear(ap.has_backup_gear || false);
          setHasInsurance(ap.has_insurance || false);
          setInsuranceCoverage(ap.insurance_coverage || "");
          setHasTransportation(ap.has_transportation !== false);
          setRateMin(ap.rate_min || "");
          setRateMax(ap.rate_max || "");
          setPortfolio(ap.portfolio || []);
          setPortfolioLink(ap.portfolio_link || "");
          setInstagramHandle(ap.instagram_handle || "");
          setFacebookUrl(ap.facebook_url || "");
          setTiktokHandle(ap.tiktok_handle || "");
          setLanguages(ap.languages || ["English"]);
          setCertifications(ap.certifications || []);
          setW9Uploaded(ap.w9_uploaded || false);
          setW9FileName(ap.w9_file_name || "");
          setPaymentMethods(ap.payment_methods || []);
          setPortfolioVideos(ap.portfolio_videos || []);
        }
      } catch (e) {
        console.error("Failed to fetch profile:", e);
      } finally {
        setInitialLoading(false);
        // Allow auto-save after initial load completes
        setTimeout(() => {
          isFirstLoad.current = false;
        }, 500);
      }
    }
    fetchProfile();
  }, [token]);

  // Build the update payload
  const buildUpdatePayload = useCallback(async () => {
    // Validate at least one role is selected
    if (!roleAssistant && !roleStudio) {
      return null;
    }

    // Geocode location if city is provided
    let coordinates = null;
    if (city?.trim()) {
      try {
        const geoResponse = await fetch(`${API}/geocode?address=${encodeURIComponent(`${city} ${state} ${zipCode}`)}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        if (geoResponse.ok) {
          const geoData = await geoResponse.json();
          if (geoData.lat && geoData.lng) {
            coordinates = [geoData.lng, geoData.lat];
          }
        }
      } catch {
        // Geocoding failed silently - location will be saved without coordinates
      }
    }

    // Build roles array
    const roles = [];
    if (roleAssistant) roles.push("assistant");
    if (roleStudio) roles.push("studio");

    // Build location object
    let locationData = null;
    if (city || state || zipCode) {
      locationData = {
        type: "Point",
        coordinates: coordinates || [],
        city: city || "",
        state: state || "",
        zip_code: zipCode || "",
        work_radius: workRadius
      };
    }

    const updateData = {
      name,
      bio,
      phone,
      profile_image: profileImage,
      roles,
      location: locationData,
      communication_preferences: {
        new_jobs: prefNewJobs,
        new_messages: prefNewMessages,
        announcements: prefAnnouncements,
        hiring: true,
      },
    };

    // Add assistant profile if user has assistant role
    if (roleAssistant) {
      const existingAvailSettings = existingProfile.availability_settings || {};
      
      updateData.assistant_profile = {
        ...existingProfile,
        find_work_active: isActive,
        years_experience: parseInt(yearsExperience) || 0,
        work_types: workTypes,
        rate_min: (() => {
          const rates = workTypes.filter(wt => typeof wt === "object" && wt.rate_min).map(wt => parseFloat(wt.rate_min));
          return rates.length > 0 ? Math.min(...rates) : (rateMin ? parseFloat(rateMin) : null);
        })(),
        rate_max: (() => {
          const rates = workTypes.filter(wt => typeof wt === "object" && wt.rate_max).map(wt => parseFloat(wt.rate_max));
          return rates.length > 0 ? Math.max(...rates) : (rateMax ? parseFloat(rateMax) : null);
        })(),
        skills: skills,
        willing_to_travel: willingToTravel,
        availability_settings: {
          ...existingAvailSettings,
          general_availability: generalAvailability,
          unavailable_dates: existingAvailSettings.unavailable_dates || [],
        },
        genres,
        shooting_styles: shootingStyles,
        editing_styles: editingStyles,
        lighting_styles: lightingStyles,
        experience_level: experienceLevel,
        employment_type: employmentType,
        has_been_lead: hasBeenLead,
        notable_clients: notableClients,
        completed_shoots: existingProfile.completed_shoots || {},
        gear_items: gearItems,
        willing_to_rent: willingToRent,
        offers_editing: offersEditing,
        has_backup_gear: hasBackupGear,
        has_insurance: hasInsurance,
        insurance_coverage: insuranceCoverage,
        has_transportation: hasTransportation,
        rate_min: rateMin ? parseFloat(rateMin) : null,
        rate_max: rateMax ? parseFloat(rateMax) : null,
        portfolio,
        portfolio_link: portfolioLink,
        instagram_handle: instagramHandle,
        facebook_url: facebookUrl,
        tiktok_handle: tiktokHandle,
        languages,
        certifications,
        insurance_verified: existingProfile.insurance_verified || false,
        work_verified: existingProfile.work_verified || false,
        response_time_hours: existingProfile.response_time_hours || null,
        w9_uploaded: w9Uploaded,
        w9_file_name: w9FileName,
        payment_methods: paymentMethods,
        portfolio_videos: portfolioVideos
      };
      
      // Legacy fields for backward compatibility
      updateData.hourly_rate = rateMin ? parseFloat(rateMin) : null;
      updateData.availability = generalAvailability !== "none";
      updateData.gear = gearItems.map(g => g.name);
      updateData.skills = workTypes.map(wt => typeof wt === "object" ? wt.type : wt);
      updateData.portfolio_images = portfolio.map(p => p.url);
      
      // Sync portfolio and gear to studio profile if user has studio role
      if (roleStudio && user?.studio_profile) {
        updateData.studio_profile = {
          ...user.studio_profile,
          portfolio: portfolio,
          portfolio_videos: portfolioVideos,
          gear_items: gearItems
        };
      }
    }

    return updateData;
  }, [
    token, name, bio, phone, profileImage, city, state, zipCode, workRadius, willingToTravel, isActive,
    prefNewJobs, prefNewMessages, prefAnnouncements, roleAssistant, roleStudio,
    yearsExperience, workTypes, skills, generalAvailability, genres, shootingStyles,
    editingStyles, lightingStyles, experienceLevel, employmentType, hasBeenLead,
    notableClients, gearItems, offersEditing, hasBackupGear, hasInsurance,
    insuranceCoverage, hasTransportation, rateMin, rateMax, portfolio,
    portfolioLink, instagramHandle, facebookUrl, tiktokHandle, languages, certifications,
    w9Uploaded, w9FileName, paymentMethods, portfolioVideos, existingProfile, user
  ]);

  // Perform the actual save (silently in the background)
  const performSave = useCallback(async () => {
    if (!token || isFirstLoad.current) return;

    setSaveStatus("saving");

    try {
      const updateData = await buildUpdatePayload();
      
      if (!updateData) {
        setSaveStatus("");
        return;
      }

      const response = await fetch(`${API}/users/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        const updatedUser = await response.json();
        updateUser(updatedUser);
        setProfileData(updatedUser);
        setSaveStatus("saved");
        // Clear "saved" status after 2 seconds
        setTimeout(() => setSaveStatus(""), 2000);
      } else {
        // SHOW ERROR - don't silently fail!
        setSaveStatus("error");
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.detail || "Failed to save profile. Please try again.";
        toast.error(errorMsg);
        console.error("Save failed:", response.status, errorData);
        // Clear error status after 3 seconds
        setTimeout(() => setSaveStatus(""), 3000);
      }
    } catch (error) {
      // SHOW ERROR - don't silently fail!
      setSaveStatus("error");
      toast.error("Failed to save profile. Please check your connection.");
      console.error("Auto-save error:", error);
      // Clear error status after 3 seconds
      setTimeout(() => setSaveStatus(""), 3000);
    }
  }, [token, buildUpdatePayload, updateUser]);

  // Debounced auto-save - triggers 500ms after last change (faster for better UX)
  const triggerAutoSave = useCallback(() => {
    if (isFirstLoad.current) return;
    
    // Show saving indicator immediately
    setSaveStatus("saving");
    
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Set new timeout - reduced to 500ms for faster saves
    saveTimeoutRef.current = setTimeout(() => {
      performSave();
    }, 500);
  }, [performSave]);

  // Immediate save - for critical operations like file uploads
  // This bypasses the debounce to prevent data loss
  const saveImmediately = useCallback(() => {
    if (isFirstLoad.current) return;
    
    // Clear any pending debounced save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Save immediately
    performSave();
  }, [performSave]);

  // Watch all form fields and trigger auto-save on changes
  useEffect(() => {
    triggerAutoSave();
    
    // Cleanup timeout on unmount
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [
    // All the fields that should trigger auto-save
    name, bio, phone, profileImage, city, state, zipCode, workRadius, isActive,
    prefNewJobs, prefNewMessages, prefAnnouncements, roleAssistant, roleStudio,
    yearsExperience, workTypes, skills, generalAvailability, genres, shootingStyles,
    editingStyles, lightingStyles, experienceLevel, employmentType, hasBeenLead,
    notableClients, gearItems, offersEditing, hasBackupGear, hasInsurance,
    insuranceCoverage, hasTransportation, rateMin, rateMax, portfolio,
    portfolioLink, instagramHandle, facebookUrl, tiktokHandle, languages, certifications,
    w9Uploaded, w9FileName, paymentMethods, portfolioVideos
  ]);

  // Calculate profile completion percentage
  const calculateCompletion = () => {
    if (!isAssistant()) return 100;
    
    // Portfolio counts if user has EITHER photos OR videos (not both required)
    const hasPortfolioContent = portfolio.length > 0 || portfolioVideos.length > 0;
    
    // External links count if user has at least ONE link (not all required)
    const hasExternalLink = portfolioLink || instagramHandle || facebookUrl || tiktokHandle;
    
    const checks = [
      !!profileImage,                              // Profile photo
      !!bio && bio.length > 10,                   // Bio
      !!city,                                      // Location
      hasPortfolioContent,                        // Portfolio (photos OR videos)
      !!rateMin,                                  // Hourly rate
      workTypes.length > 0,                       // Work types
      !!yearsExperience && yearsExperience > 0,  // Years experience
      gearItems.length > 0,                       // Gear
      genres.length > 0,                          // Genres
      shootingStyles.length > 0,                  // Shooting styles
      // NOTE: External links and certifications are NOT required for 100%
    ];
    
    const completed = checks.filter(Boolean).length;
    return Math.round((completed / checks.length) * 100);
  };

  const profileCompletion = calculateCompletion();

  const handleLogout = async () => {
    await logout();
    window.location.href = "/";
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-white text-xl">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <NavBar>
        <Link to="/dashboard" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider">Dashboard</Link>
        <NavActions />
      </NavBar>

      <main className="flex-1 pt-24 pb-12 px-6 md:px-12">
        {/* Save Status Indicator */}
        {saveStatus && (
          <div className={`fixed top-20 right-6 z-50 flex items-center gap-2 px-3 py-2 rounded-sm text-sm ${
            saveStatus === "saving" ? "bg-yellow-500/20 text-yellow-400" : 
            saveStatus === "error" ? "bg-red-500/20 text-red-400" :
            "bg-green-500/20 text-green-400"
          }`}>
            {saveStatus === "saving" ? (
              <>
                <div className="w-3 h-3 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : saveStatus === "error" ? (
              <>
                <span className="text-red-400">✕</span>
                Save Failed
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Saved
              </>
            )}
          </div>
        )}

        <div className="max-w-4xl mx-auto">
          <Link to={`/profile/${user.user_id}`} className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-8" data-testid="back-to-profile">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm uppercase tracking-wider">Back to Profile</span>
          </Link>

          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">Edit Profile</h1>
          <p className="text-white/50 mb-4">Complete your profile to get discovered by studios</p>

          {/* Active Status Toggle */}
          <div className="flex items-center justify-between p-4 mb-4 bg-white/[0.03] border border-white/10 rounded" data-testid="active-toggle-section">
            <div>
              <p className="font-medium text-sm">For-Hire Profile Active</p>
              <p className="text-white/40 text-xs mt-0.5">When off, you won't appear in candidate searches or for-hire results</p>
            </div>
            <button
              type="button"
              onClick={() => setIsActive(!isActive)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${isActive ? "bg-[#e8644a]" : "bg-white/15"}`}
              data-testid="active-toggle"
            >
              <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${isActive ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
          
          {/* Link to Studio Profile if user is a studio */}
          {isStudio() && (
            <Link 
              to="/profile/edit/studio" 
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/[0.05] border border-white/20 rounded-sm text-white/80 hover:text-white hover:border-white/40 transition-all mb-8"
              data-testid="edit-studio-profile-link"
            >
              <Briefcase className="w-4 h-4" />
              <span>Edit Studio Profile</span>
              <ArrowLeft className="w-4 h-4 rotate-180" />
            </Link>
          )}

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex items-center justify-between mb-4">
              <TabsList className="bg-white/[0.03] border border-white/10 flex-wrap h-auto p-1">
                <TabsTrigger value="basic" className="data-[state=active]:bg-white data-[state=active]:text-black">
                  <Camera className="w-4 h-4 mr-2" />Basic
                </TabsTrigger>
                {isAssistant() && (
                  <>
                    <TabsTrigger value="experience" className="data-[state=active]:bg-white data-[state=active]:text-black">
                      <Award className="w-4 h-4 mr-2" />Experience
                    </TabsTrigger>
                    <TabsTrigger value="gear" className="data-[state=active]:bg-white data-[state=active]:text-black">
                      <Settings className="w-4 h-4 mr-2" />Gear
                    </TabsTrigger>
                    <TabsTrigger value="professional" className="data-[state=active]:bg-white data-[state=active]:text-black">
                      <Briefcase className="w-4 h-4 mr-2" />Professional
                    </TabsTrigger>
                    <TabsTrigger value="portfolio" className="data-[state=active]:bg-white data-[state=active]:text-black">
                      <Globe className="w-4 h-4 mr-2" />Portfolio
                    </TabsTrigger>
                    <TabsTrigger value="payments" className="data-[state=active]:bg-white data-[state=active]:text-black">
                      <DollarSign className="w-4 h-4 mr-2" />Payments
                    </TabsTrigger>
                  </>
                )}
                <TabsTrigger value="account" className="data-[state=active]:bg-red-500/20 data-[state=active]:text-red-400">
                  <Settings className="w-4 h-4 mr-2" />Account
                </TabsTrigger>
              </TabsList>
              
              {/* Profile Completion Badge */}
              {isAssistant() && (
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                  profileCompletion === 100 
                    ? "bg-white/20 text-white" 
                    : profileCompletion >= 70 
                      ? "bg-white/15 text-white/80"
                      : "bg-coral/20 text-coral"
                }`}>
                  <span>{profileCompletion}% Complete</span>
                </div>
              )}
            </div>

            {/* Basic Info Tab */}
            <TabsContent value="basic">
              <BasicInfoSection
                name={name}
                setName={setName}
                bio={bio}
                setBio={setBio}
                phone={phone}
                setPhone={setPhone}
                profileImage={profileImage}
                setProfileImage={setProfileImage}
                token={token}
                onImageUploaded={saveImmediately}
              />

              <LocationInput
                city={city}
                setCity={setCity}
                state={state}
                setState={setState}
                zipCode={zipCode}
                setZipCode={setZipCode}
                workRadius={workRadius}
                setWorkRadius={setWorkRadius}
                willingToTravel={willingToTravel}
                setWillingToTravel={setWillingToTravel}
              />

              {isAssistant() && (
                <>
                  <WorkTypesSection 
                    workTypes={workTypes} 
                    setWorkTypes={setWorkTypes} 
                  />
                  <SkillsSection
                    skills={skills}
                    setSkills={setSkills}
                  />
                  <AvailabilitySection 
                    generalAvailability={generalAvailability} 
                    setGeneralAvailability={setGeneralAvailability} 
                  />
                  
                  {/* Google Calendar Connection */}
                  <div className="mt-8">
                    <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-coral" />
                      Calendar Integration
                    </h3>
                    <CalendarConnection />
                  </div>
                </>
              )}

              <div className="flex justify-end mt-6">
                <Button 
                  onClick={() => setActiveTab("experience")} 
                  type="button" 
                  className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold px-8"
                >
                  Next: Experience
                </Button>
              </div>
            </TabsContent>

            {/* Experience Tab */}
            {isAssistant() && (
              <TabsContent value="experience">
                <ExperienceSection
                  experienceLevel={experienceLevel}
                  setExperienceLevel={setExperienceLevel}
                  yearsExperience={yearsExperience}
                  setYearsExperience={setYearsExperience}
                  employmentType={employmentType}
                  setEmploymentType={setEmploymentType}
                  hasBeenLead={hasBeenLead}
                  setHasBeenLead={setHasBeenLead}
                />

                <GenresSection 
                  genres={genres} 
                  setGenres={setGenres} 
                />

                <ShootingStylesSection 
                  shootingStyles={shootingStyles} 
                  setShootingStyles={setShootingStyles}
                  editingStyles={editingStyles}
                  setEditingStyles={setEditingStyles}
                  lightingStyles={lightingStyles}
                  setLightingStyles={setLightingStyles}
                />

                <NotableClientsSection 
                  notableClients={notableClients} 
                  setNotableClients={setNotableClients} 
                />

                <div className="flex justify-between mt-6">
                  <Button 
                    onClick={() => setActiveTab("basic")} 
                    type="button" 
                    variant="outline" 
                    className="border-white/20 text-white hover:bg-white/10 rounded-sm px-8"
                  >
                    Back
                  </Button>
                  <Button 
                    onClick={() => setActiveTab("gear")} 
                    type="button" 
                    className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold px-8"
                  >
                    Next: Gear
                  </Button>
                </div>
              </TabsContent>
            )}

            {/* Gear Tab */}
            {isAssistant() && (
              <TabsContent value="gear">
                <GearBagSection 
                  gearItems={gearItems} 
                  setGearItems={setGearItems}
                  willingToRent={willingToRent}
                  setWillingToRent={setWillingToRent}
                />
                {isStudio() && (
                  <p className="text-white/40 text-sm mb-6">
                    💡 Your gear is shared between your For-Hire and Studio profiles.
                  </p>
                )}

                <AdditionalServicesSection
                  hasBackupGear={hasBackupGear}
                  setHasBackupGear={setHasBackupGear}
                  offersEditing={offersEditing}
                  setOffersEditing={setOffersEditing}
                />

                <div className="flex justify-between mt-6">
                  <Button 
                    onClick={() => setActiveTab("experience")} 
                    type="button" 
                    variant="outline" 
                    className="border-white/20 text-white hover:bg-white/10 rounded-sm px-8"
                  >
                    Back
                  </Button>
                  <Button 
                    onClick={() => setActiveTab("professional")} 
                    type="button" 
                    className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold px-8"
                  >
                    Next: Professional
                  </Button>
                </div>
              </TabsContent>
            )}

            {/* Professional Tab */}
            {isAssistant() && (
              <TabsContent value="professional">
                <ProfessionalDetailsSection
                  hasInsurance={hasInsurance}
                  setHasInsurance={setHasInsurance}
                  insuranceCoverage={insuranceCoverage}
                  setInsuranceCoverage={setInsuranceCoverage}
                  hasTransportation={hasTransportation}
                  setHasTransportation={setHasTransportation}
                />

                <LanguagesSection 
                  languages={languages} 
                  setLanguages={setLanguages} 
                />

                <CertificationsSection 
                  certifications={certifications} 
                  setCertifications={setCertifications} 
                />

                <div className="flex justify-between mt-6">
                  <Button 
                    onClick={() => setActiveTab("gear")} 
                    type="button" 
                    variant="outline" 
                    className="border-white/20 text-white hover:bg-white/10 rounded-sm px-8"
                  >
                    Back
                  </Button>
                  <Button 
                    onClick={() => setActiveTab("portfolio")} 
                    type="button" 
                    className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold px-8"
                  >
                    Next: Portfolio
                  </Button>
                </div>
              </TabsContent>
            )}

            {/* Portfolio Tab */}
            {isAssistant() && (
              <TabsContent value="portfolio">
                <PortfolioSection
                  portfolio={portfolio}
                  setPortfolio={setPortfolio}
                  token={token}
                  onImagesUploaded={saveImmediately}
                />
                
                <div className="mt-10 pt-8 border-t border-white/10">
                  <VideoPortfolioSection
                    videos={portfolioVideos}
                    setVideos={setPortfolioVideos}
                  />
                </div>

                <ExternalLinksSection
                  portfolioLink={portfolioLink}
                  setPortfolioLink={setPortfolioLink}
                  instagramHandle={instagramHandle}
                  setInstagramHandle={setInstagramHandle}
                  facebookUrl={facebookUrl}
                  setFacebookUrl={setFacebookUrl}
                  tiktokHandle={tiktokHandle}
                  setTiktokHandle={setTiktokHandle}
                />

                <div className="flex justify-between mt-6">
                  <Button 
                    onClick={() => setActiveTab("professional")} 
                    type="button" 
                    variant="outline" 
                    className="border-white/20 text-white hover:bg-white/10 rounded-sm px-8"
                  >
                    Back
                  </Button>
                  <Button 
                    onClick={() => setActiveTab("payments")} 
                    type="button" 
                    className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold px-8"
                  >
                    Next: Payments
                  </Button>
                </div>
              </TabsContent>
            )}

            {/* Payments Tab */}
            {isAssistant() && (
              <TabsContent value="payments">
                <PaymentsSection
                  w9Uploaded={w9Uploaded}
                  setW9Uploaded={setW9Uploaded}
                  w9FileName={w9FileName}
                  setW9FileName={setW9FileName}
                  paymentMethods={paymentMethods}
                  setPaymentMethods={setPaymentMethods}
                  token={token}
                  API={API}
                />

                <div className="flex justify-between mt-6">
                  <Button 
                    onClick={() => setActiveTab("portfolio")} 
                    type="button" 
                    variant="outline" 
                    className="border-white/20 text-white hover:bg-white/10 rounded-sm px-8"
                  >
                    Back
                  </Button>
                  <Link to={`/profile/${user.user_id}`}>
                    <Button 
                      type="button" 
                      className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold px-8"
                    >
                      Done
                    </Button>
                  </Link>
                </div>
              </TabsContent>
            )}

            {/* Account Management Tab */}
            <TabsContent value="account">
              <AccountManagement token={token} user={user} logout={logout} navigate={navigate} />
            </TabsContent>

            {/* Submit for non-assistants */}
            {!isAssistant() && (
              <div className="flex justify-end mt-6">
                <Link to={`/profile/${user.user_id}`}>
                  <Button 
                    type="button" 
                    className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold px-8"
                  >
                    Done
                  </Button>
                </Link>
              </div>
            )}
          </Tabs>
        </div>
      </main>
      <Footer />
    </div>
  );
}
